import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  TrendingDown, TrendingUp, Wallet, AlertTriangle, Target,
  Plus, X, Sparkles, ArrowUpCircle, ArrowDownCircle, Check,
  FlaskConical, Gauge, FileDown, Mail, Loader2, RefreshCw
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  profitable:    { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Profitable' },
  healthy:       { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Healthy' },
  warning:       { color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200',   label: 'Warning' },
  critical:      { color: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200',     label: 'Critical' },
  negative_cash: { color: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200',     label: 'Negative Cash' },
};

const BUDGET_STATUS_STYLES = {
  on_track:      { color: 'text-emerald-700', bar: 'bg-emerald-500', label: 'On track' },
  slightly_over: { color: 'text-amber-700',   bar: 'bg-amber-500',   label: 'Slightly over' },
  over:          { color: 'text-red-700',     bar: 'bg-red-500',     label: 'Over budget' },
  well_under:    { color: 'text-blue-700',    bar: 'bg-blue-400',    label: 'Well under' },
  no_budget:     { color: 'text-gray-500',    bar: 'bg-gray-300',    label: 'No budget' },
};

const ANOMALY_STYLES = {
  high:     { icon: AlertTriangle, color: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200' },
  medium:   { icon: AlertTriangle, color: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200' },
  low:      { icon: AlertTriangle, color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200' },
};

const ANOMALY_TYPE_LABELS = {
  vendor_spike: 'Vendor Spike',
  new_vendor_large: 'New Large Vendor',
  duplicate_charge: 'Possible Duplicate',
};

const CFOTab = ({ token, selectedBusiness, expenseCategories = [], formatCurrency }) => {
  const [runway, setRunway] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newBudgetCatId, setNewBudgetCatId] = useState('');
  const [newBudgetAmount, setNewBudgetAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const [errors, setErrors] = useState({ runway: null, budget: null, anomalies: null });

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchOne = useCallback(async (key, path, setter) => {
    setErrors((prev) => ({ ...prev, [key]: null }));
    try {
      const r = await axios.get(`${API}${path}`, authHeaders);
      setter(r.data);
    } catch (err) {
      setErrors((prev) => ({ ...prev, [key]: err.response?.data?.detail || 'Failed to load' }));
      setter(null);
    }
  }, [authHeaders]);

  const fetchAll = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    setLoading(true);
    await Promise.allSettled([
      fetchOne('runway', `/api/mybookie/cfo/runway/${selectedBusiness.id}`, setRunway),
      fetchOne('budget', `/api/mybookie/cfo/budget-vs-actuals/${selectedBusiness.id}`, setBudgetData),
      fetchOne('anomalies', `/api/mybookie/cfo/anomalies/${selectedBusiness.id}?days=30`, setAnomalies),
    ]);
    setLoading(false);
  }, [selectedBusiness, fetchOne]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSaveBudget = async () => {
    if (!newBudgetCatId || !newBudgetAmount) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/mybookie/cfo/budgets`, {
        business_id: selectedBusiness.id,
        category_id: newBudgetCatId,
        monthly_amount: parseFloat(newBudgetAmount),
      }, authHeaders);
      toast({ title: 'Budget saved' });
      setNewBudgetCatId('');
      setNewBudgetAmount('');
      fetchAll();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveBudget = async (categoryId) => {
    try {
      await axios.delete(`${API}/api/mybookie/cfo/budgets/${selectedBusiness.id}/${categoryId}`, authHeaders);
      toast({ title: 'Budget removed' });
      fetchAll();
    } catch (err) {
      toast({ title: 'Remove failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-[#5C6661]" data-testid="cfo-loading">Loading CFO insights...</div>;
  }

  // ============ RUNWAY CARD ============
  const runwayStyle = runway ? STATUS_STYLES[runway.runway_status] || STATUS_STYLES.healthy : STATUS_STYLES.healthy;
  const burnChange = runway?.burn_change_pct;

  const ErrorBox = ({ message, onRetry, testid }) => (
    <div className="flex items-center justify-between gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800" data-testid={testid}>
      <span><AlertTriangle className="w-4 h-4 inline mr-1.5" /> {message}</span>
      <Button size="sm" variant="outline" onClick={onRetry} className="bg-white text-red-700 border-red-300 hover:bg-red-100">
        <RefreshCw className="w-3 h-3 mr-1.5" /> Retry
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="cfo-tab">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Sparkles className="w-6 h-6 text-[#0A2B1D] mt-1" />
        <div>
          <h2 className="text-2xl font-semibold text-[#0A2B1D]">CFO Insights</h2>
          <p className="text-sm text-[#5C6661] mt-1">
            Burn rate, runway, budget tracking, and automated anomaly detection — your AI finance team at a glance.
          </p>
        </div>
      </div>

      {/* RUNWAY + BURN RATE */}
      <Card className={`border-2 ${runwayStyle.border} ${runwayStyle.bg}`} data-testid="cfo-runway-card">
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wallet className={`w-5 h-5 ${runwayStyle.color}`} />
                Runway & Burn Rate
              </CardTitle>
              <CardDescription>3-month rolling average · {runway?.business_name}</CardDescription>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${runwayStyle.color} bg-white border ${runwayStyle.border}`} data-testid="cfo-runway-status">
              {runwayStyle.label}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {errors.runway ? (
            <ErrorBox
              message={`Runway: ${errors.runway}`}
              onRetry={() => fetchOne('runway', `/api/mybookie/cfo/runway/${selectedBusiness.id}`, setRunway)}
              testid="cfo-runway-error"
            />
          ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-[#5C6661] mb-1">Runway</div>
              <div className={`text-2xl font-bold ${runwayStyle.color}`} data-testid="cfo-runway-months">
                {runway?.runway_months === null
                  ? '∞'
                  : runway?.runway_months === 0
                  ? '0 mo'
                  : `${runway?.runway_months} mo`}
              </div>
              {runway?.runway_status === 'profitable' && (
                <div className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1"><Check className="w-3 h-3" /> Cashflow positive</div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#5C6661] mb-1">Monthly Burn</div>
              <div className="text-2xl font-bold text-[#111614]" data-testid="cfo-monthly-burn">
                {formatCurrency(runway?.net_burn || 0)}
              </div>
              {burnChange !== null && burnChange !== undefined && (
                <div className={`text-xs mt-0.5 flex items-center gap-1 ${burnChange > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                  {burnChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {burnChange > 0 ? '+' : ''}{burnChange}% vs prev month
                </div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#5C6661] mb-1">Avg Income</div>
              <div className="text-2xl font-bold text-emerald-700" data-testid="cfo-avg-income">
                {formatCurrency(runway?.avg_monthly_income || 0)}
              </div>
              <div className="text-xs text-[#5C6661] mt-0.5">3-mo avg</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#5C6661] mb-1">Estimated Cash</div>
              <div className="text-2xl font-bold text-[#0A2B1D]" data-testid="cfo-estimated-cash">
                {formatCurrency(runway?.estimated_cash || 0)}
              </div>
              <div className="text-xs text-[#5C6661] mt-0.5">Net of all-time activity</div>
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      {/* BUDGET vs ACTUALS */}
      <Card data-testid="cfo-budget-card">
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-[#0A2B1D]" /> Budget vs Actuals</CardTitle>
              <CardDescription>{budgetData?.label} · Set monthly category budgets and track variance</CardDescription>
            </div>
            {budgetData?.totals?.variance_pct !== null && budgetData?.totals?.budget > 0 && (
              <div className="text-right">
                <div className="text-xs uppercase text-[#5C6661]">Total</div>
                <div className={`text-lg font-bold ${budgetData.totals.variance > 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="cfo-budget-total-variance">
                  {budgetData.totals.variance > 0 ? '+' : ''}{formatCurrency(budgetData.totals.variance)} ({budgetData.totals.variance_pct}%)
                </div>
                <div className="text-xs text-[#5C6661]">
                  {formatCurrency(budgetData.totals.actual)} / {formatCurrency(budgetData.totals.budget)}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {errors.budget ? (
            <ErrorBox
              message={`Budgets: ${errors.budget}`}
              onRetry={() => fetchOne('budget', `/api/mybookie/cfo/budget-vs-actuals/${selectedBusiness.id}`, setBudgetData)}
              testid="cfo-budget-error"
            />
          ) : (<>
          {/* Add budget row */}
          {expenseCategories.length === 0 ? (
            <div className="text-center py-8 text-[#5C6661] text-sm bg-[#F6F5F2] rounded-md border border-dashed border-[#D5CFC1]" data-testid="cfo-budget-no-categories">
              <Target className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="font-medium text-[#111614]">No expense categories yet</p>
              <p className="mt-1">Add categories to your business first — then you can set monthly budgets here.</p>
            </div>
          ) : (
          <div className="flex flex-col sm:flex-row gap-2 mb-4 p-3 rounded-md bg-[#F6F5F2] border border-[#E6E8E6]">
            <Select value={newBudgetCatId} onValueChange={setNewBudgetCatId}>
              <SelectTrigger className="bg-white sm:w-[240px]" data-testid="cfo-budget-category-select">
                <SelectValue placeholder="Pick a category..." />
              </SelectTrigger>
              <SelectContent>
                {expenseCategories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="Monthly budget $"
              value={newBudgetAmount}
              onChange={(e) => setNewBudgetAmount(e.target.value)}
              className="bg-white sm:w-[180px]"
              data-testid="cfo-budget-amount-input"
            />
            <Button
              onClick={handleSaveBudget}
              disabled={saving || !newBudgetCatId || !newBudgetAmount}
              className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
              data-testid="cfo-budget-save-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Set Budget'}
            </Button>
          </div>
          )}

          {/* Budget rows */}
          {(!budgetData?.categories || budgetData.categories.length === 0) ? (
            <div className="text-center py-8 text-[#5C6661] text-sm" data-testid="cfo-budget-empty">
              <Target className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              No budgets set yet. Add one above to start tracking variance.
            </div>
          ) : (
            <div className="space-y-3" data-testid="cfo-budget-list">
              {budgetData.categories.map((row) => {
                const bStyle = BUDGET_STATUS_STYLES[row.status] || BUDGET_STATUS_STYLES.on_track;
                const pct = row.budget > 0 ? Math.min((row.actual / row.budget) * 100, 200) : 0;
                return (
                  <div key={row.category_id} className="p-3 rounded-md border border-[#E6E8E6]" data-testid={`cfo-budget-row-${row.category_id}`}>
                    <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                      <div className="font-medium text-sm text-[#111614]">{row.category_name}</div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-[#5C6661]">
                          {formatCurrency(row.actual)} / {formatCurrency(row.budget)}
                        </span>
                        {row.variance_pct !== null && (
                          <span className={`font-medium ${bStyle.color}`}>
                            {row.variance > 0 ? '+' : ''}{row.variance_pct}%
                          </span>
                        )}
                        <button
                          onClick={() => handleRemoveBudget(row.category_id)}
                          className="text-[#5C6661] hover:text-red-600"
                          aria-label="Remove budget"
                          data-testid={`cfo-budget-remove-${row.category_id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {row.budget > 0 && (
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${bStyle.bar} transition-all duration-500`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    )}
                    <div className={`text-xs mt-1 ${bStyle.color}`}>{bStyle.label}</div>
                  </div>
                );
              })}
            </div>
          )}
          </>)}
        </CardContent>
      </Card>

      {/* ANOMALY DETECTION */}
      <Card data-testid="cfo-anomalies-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#0A2B1D]" /> Anomaly Detection</CardTitle>
          <CardDescription>
            Last 30 days · {anomalies?.expenses_scanned || 0} transactions scanned · {anomalies?.anomaly_count || 0} flagged
          </CardDescription>
        </CardHeader>
        <CardContent>
          {errors.anomalies ? (
            <ErrorBox
              message={`Anomalies: ${errors.anomalies}`}
              onRetry={() => fetchOne('anomalies', `/api/mybookie/cfo/anomalies/${selectedBusiness.id}?days=30`, setAnomalies)}
              testid="cfo-anomalies-error"
            />
          ) : (!anomalies?.anomalies || anomalies.anomalies.length === 0) ? (
            <div className="text-center py-8 text-[#5C6661] text-sm" data-testid="cfo-anomalies-empty">
              <Check className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
              No anomalies detected. Your spending looks normal.
            </div>
          ) : (
            <div className="space-y-2" data-testid="cfo-anomalies-list">
              {anomalies.anomalies.map((a, idx) => {
                const aStyle = ANOMALY_STYLES[a.severity] || ANOMALY_STYLES.low;
                const AIcon = aStyle.icon;
                const isIncrease = a.type === 'vendor_spike';
                return (
                  <div
                    key={`${a.type}-${a.vendor}-${idx}`}
                    className={`flex items-start gap-3 p-3 rounded-md ${aStyle.bg} ${aStyle.border} border`}
                    data-testid={`cfo-anomaly-item-${idx}`}
                  >
                    <AIcon className={`w-5 h-5 ${aStyle.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold uppercase ${aStyle.color}`}>
                          {ANOMALY_TYPE_LABELS[a.type] || a.type}
                        </span>
                        <span className="text-xs text-[#5C6661]">{a.severity}</span>
                      </div>
                      <p className={`mt-1 text-sm ${aStyle.color}`}>{a.reason}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-[#5C6661]">
                        <span className="font-medium">{formatCurrency(a.amount)}</span>
                        {isIncrease ? <ArrowUpCircle className="w-3 h-3" /> : <ArrowDownCircle className="w-3 h-3" />}
                        {a.baseline > 0 && <span>vs {formatCurrency(a.baseline)} baseline</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* PHASE 2 / 3 SECTIONS */}
      <Phase2Phase3Sections
        token={token}
        selectedBusiness={selectedBusiness}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

// Phase 2/3: KPI commentary, Scenario planning, AI Finance Brief, Board Pack download
const Phase2Phase3Sections = ({ token, selectedBusiness, formatCurrency }) => {
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(true);
  const [briefRefreshing, setBriefRefreshing] = useState(false);
  const [scenario, setScenario] = useState(null);
  const [scenarioRunning, setScenarioRunning] = useState(false);
  const [scenarioInput, setScenarioInput] = useState({
    label: 'Hire engineer @ $5k/mo',
    monthly_expense_delta: 5000,
    monthly_income_delta: 0,
    one_time_cash_injection: 0,
    months_ahead: 12,
  });
  const [boardPackBusy, setBoardPackBusy] = useState(false);
  const { toast } = useToast();

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const fetchKpis = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    setKpisLoading(true);
    try {
      const r = await axios.get(`${API}/api/mybookie/cfo/kpis/${selectedBusiness.id}`, authHeaders);
      setKpis(r.data);
    } catch (err) {
      console.error('KPI fetch failed:', err);
    } finally {
      setKpisLoading(false);
    }
  }, [selectedBusiness, authHeaders]);

  const fetchBrief = useCallback(async (showSpinner = true) => {
    if (!selectedBusiness?.id) return;
    if (showSpinner) setBriefLoading(true);
    else setBriefRefreshing(true);
    try {
      const r = await axios.get(`${API}/api/mybookie/cfo/finance-brief/${selectedBusiness.id}`, authHeaders);
      setBrief(r.data);
    } catch (err) {
      console.error('Brief fetch failed:', err);
    } finally {
      setBriefLoading(false);
      setBriefRefreshing(false);
    }
  }, [selectedBusiness, authHeaders]);

  useEffect(() => { fetchKpis(); fetchBrief(); }, [fetchKpis, fetchBrief]);

  const handleRunScenario = async () => {
    setScenarioRunning(true);
    try {
      const r = await axios.post(`${API}/api/mybookie/cfo/scenarios`, {
        business_id: selectedBusiness.id,
        ...scenarioInput,
        monthly_expense_delta: parseFloat(scenarioInput.monthly_expense_delta) || 0,
        monthly_income_delta: parseFloat(scenarioInput.monthly_income_delta) || 0,
        one_time_cash_injection: parseFloat(scenarioInput.one_time_cash_injection) || 0,
        months_ahead: parseInt(scenarioInput.months_ahead) || 12,
      }, authHeaders);
      setScenario(r.data);
    } catch (err) {
      toast({ title: 'Scenario failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setScenarioRunning(false);
    }
  };

  const handleDownloadBoardPack = async () => {
    setBoardPackBusy(true);
    try {
      const r = await axios.get(`${API}/api/mybookie/cfo/board-pack/${selectedBusiness.id}`, {
        ...authHeaders,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `board-pack-${selectedBusiness.name?.replace(/\s+/g, '-') || 'business'}-${new Date().toISOString().slice(0, 7)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Board Pack downloaded' });
    } catch (err) {
      toast({ title: 'Download failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setBoardPackBusy(false);
    }
  };

  return (
    <>
      {/* KPI DASHBOARD + AI COMMENTARY */}
      <Card data-testid="cfo-kpis-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Gauge className="w-5 h-5 text-[#0A2B1D]" /> Key Metrics + AI Commentary</CardTitle>
          <CardDescription>{kpis?.kpis?.current_month_label || ''} · core CFO KPIs with a written take from your AI CFO</CardDescription>
        </CardHeader>
        <CardContent>
          {kpisLoading ? (
            <div className="flex items-center gap-2 text-sm text-[#5C6661]"><Loader2 className="w-4 h-4 animate-spin" /> Computing...</div>
          ) : kpis ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <KPIBlock label="Revenue (mo)" value={formatCurrency(kpis.kpis.mrr)} change={kpis.kpis.mrr_change_pct} testid="cfo-kpi-mrr" />
                <KPIBlock label="Expenses (mo)" value={formatCurrency(kpis.kpis.monthly_expenses)} change={kpis.kpis.expenses_change_pct} invertColor testid="cfo-kpi-expenses" />
                <KPIBlock label="Gross Margin" value={kpis.kpis.gross_margin_pct !== null ? `${kpis.kpis.gross_margin_pct}%` : '—'} testid="cfo-kpi-margin" />
                <KPIBlock label="Burn Multiple" value={kpis.kpis.burn_multiple !== null ? `${kpis.kpis.burn_multiple}x` : '—'} testid="cfo-kpi-burn-multiple" />
              </div>
              <div className="p-4 rounded-md bg-[#F4F1EA] border-l-4 border-[#0A2B1D]" data-testid="cfo-ai-commentary">
                <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase text-[#0A2B1D]">
                  <Sparkles className="w-3.5 h-3.5" /> AI CFO Commentary
                </div>
                <p className="text-sm text-[#111614] leading-relaxed">{kpis.ai_commentary}</p>
              </div>
            </>
          ) : (
            <div className="text-sm text-[#5C6661]">Could not load KPIs.</div>
          )}
        </CardContent>
      </Card>

      {/* AI FINANCE BRIEF */}
      <Card data-testid="cfo-brief-card">
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#0A2B1D]" /> AI Finance Brief</CardTitle>
              <CardDescription>Top 3 things to know this week · paid users also receive this via email every 7 days</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => fetchBrief(false)} disabled={briefRefreshing} data-testid="cfo-brief-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${briefRefreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {briefLoading ? (
            <div className="flex items-center gap-2 text-sm text-[#5C6661]"><Loader2 className="w-4 h-4 animate-spin" /> Asking AI CFO...</div>
          ) : (
            <div className="space-y-3" data-testid="cfo-brief-list">
              {(brief?.items || []).map((item, idx) => (
                <div key={idx} className="p-3 rounded-md bg-[#F6F5F2] border-l-4 border-[#0A2B1D]" data-testid={`cfo-brief-item-${idx}`}>
                  <div className="flex items-start gap-2">
                    <div className="text-xs font-bold text-[#0A2B1D] bg-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">{idx + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-[#111614]">{item.title}</div>
                      <p className="text-sm text-[#5C6661] mt-1 leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                </div>
              ))}
              {(!brief?.items || brief.items.length === 0) && (
                <div className="text-sm text-[#5C6661]">No insights available — add a few transactions and check back.</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SCENARIO PLANNING */}
      <Card data-testid="cfo-scenarios-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FlaskConical className="w-5 h-5 text-[#0A2B1D]" /> Scenario Planning</CardTitle>
          <CardDescription>Model what-if changes to expenses, income, and cash to see runway impact</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="text-xs text-[#5C6661] mb-1 block">Label</label>
              <Input
                value={scenarioInput.label}
                onChange={(e) => setScenarioInput({ ...scenarioInput, label: e.target.value })}
                className="bg-white text-sm"
                data-testid="cfo-scenario-label"
              />
            </div>
            <div>
              <label className="text-xs text-[#5C6661] mb-1 block">Expense Δ ($/mo)</label>
              <Input
                type="number"
                value={scenarioInput.monthly_expense_delta}
                onChange={(e) => setScenarioInput({ ...scenarioInput, monthly_expense_delta: e.target.value })}
                className="bg-white text-sm"
                data-testid="cfo-scenario-expense-delta"
              />
            </div>
            <div>
              <label className="text-xs text-[#5C6661] mb-1 block">Income Δ ($/mo)</label>
              <Input
                type="number"
                value={scenarioInput.monthly_income_delta}
                onChange={(e) => setScenarioInput({ ...scenarioInput, monthly_income_delta: e.target.value })}
                className="bg-white text-sm"
                data-testid="cfo-scenario-income-delta"
              />
            </div>
            <div>
              <label className="text-xs text-[#5C6661] mb-1 block">One-time cash ($)</label>
              <Input
                type="number"
                value={scenarioInput.one_time_cash_injection}
                onChange={(e) => setScenarioInput({ ...scenarioInput, one_time_cash_injection: e.target.value })}
                className="bg-white text-sm"
                data-testid="cfo-scenario-injection"
              />
            </div>
          </div>
          <Button
            onClick={handleRunScenario}
            disabled={scenarioRunning}
            className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
            data-testid="cfo-scenario-run"
          >
            {scenarioRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-2" />}
            Run Scenario
          </Button>
          {scenario && (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="cfo-scenario-result">
              <div className="p-4 rounded-md bg-[#F6F5F2] border border-[#E6E8E6]">
                <div className="text-xs uppercase text-[#5C6661] mb-2">Baseline (current)</div>
                <div className="space-y-1 text-sm">
                  <div>Burn: <span className="font-medium">{formatCurrency(scenario.baseline.monthly_burn)}/mo</span></div>
                  <div>Runway: <span className="font-medium">{scenario.baseline.runway_months === null ? '∞' : `${scenario.baseline.runway_months} mo`}</span></div>
                </div>
              </div>
              <div className="p-4 rounded-md bg-[#E2ECE7] border border-[#0A2B1D]/20">
                <div className="text-xs uppercase text-[#0A2B1D] mb-2 font-medium">{scenario.label}</div>
                <div className="space-y-1 text-sm">
                  <div>Burn: <span className="font-medium">{formatCurrency(scenario.scenario.monthly_burn)}/mo</span></div>
                  <div>Runway: <span className="font-medium">{scenario.scenario.runway_months === null ? '∞' : `${scenario.scenario.runway_months} mo`}</span></div>
                  {scenario.delta.runway_change_months !== null && (
                    <div className={`text-xs mt-2 ${scenario.delta.runway_change_months < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                      Δ Runway: {scenario.delta.runway_change_months > 0 ? '+' : ''}{scenario.delta.runway_change_months} mo
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* BOARD PACK PDF */}
      <Card data-testid="cfo-boardpack-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileDown className="w-5 h-5 text-[#0A2B1D]" /> Board / Investor Pack</CardTitle>
          <CardDescription>One-click PDF for monthly investor updates — KPIs, runway, anomalies, AI commentary</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleDownloadBoardPack}
            disabled={boardPackBusy}
            className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
            data-testid="cfo-boardpack-download"
          >
            {boardPackBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            {boardPackBusy ? 'Generating...' : 'Download PDF (this month)'}
          </Button>
          <p className="text-xs text-[#5C6661] mt-3">
            PDF includes: Executive Summary (AI-written) · KPIs vs prior month · Runway · Top 5 expense categories · Anomalies flagged.
          </p>
        </CardContent>
      </Card>
    </>
  );
};

const KPIBlock = ({ label, value, change, invertColor, testid }) => {
  const positive = (change ?? 0) > 0;
  const showColor = change !== null && change !== undefined;
  const goodIsUp = !invertColor;
  const colorClass = showColor
    ? (positive === goodIsUp ? 'text-emerald-700' : 'text-red-700')
    : 'text-[#5C6661]';
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-[#5C6661] mb-1">{label}</div>
      <div className="text-xl font-bold text-[#111614]">{value}</div>
      {showColor && (
        <div className={`text-xs mt-0.5 flex items-center gap-1 ${colorClass}`}>
          <Icon className="w-3 h-3" />
          {change > 0 ? '+' : ''}{change}% vs prev
        </div>
      )}
    </div>
  );
};

export default CFOTab;
